import React, { useEffect, useState, useCallback, useRef } from "react";
import { auth, db } from "@/config/firebase";
import { doc, onSnapshot, getDoc } from "firebase/firestore";
import { cn } from "@/lib/utils";
import { CoinDisplay } from "./OddsBadge";
import { PlayerAvatar } from "@/components/PlayerAvatar";
import { Trophy, Medal, Award, Crown } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Leaderboard - Global economy leaderboard
 * 
 * Realtime updates via polling (Firestore rules don't allow reading all wallets).
 * Polls every 2 seconds while visible.
 * 
 * Props:
 * - seasonId: string (default: "2026-27")
 * - maxEntries: number (default: 20)
 * - showDetails: boolean (show reserved/available)
 * - highlightUserId: string (highlight current user)
 * - compact: boolean (compact mode for mobile)
 * - realtime: boolean (use polling for realtime updates)
 * - className: additional CSS classes
 */
export function Leaderboard({
  seasonId = "2026-27",
  maxEntries = 20,
  showDetails = false,
  highlightUserId = null,
  compact = false,
  realtime = true,
  adminMode = false,
  className,
}) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const pollIntervalRef = useRef(null);
  const isMountedRef = useRef(true);

  // Get current user
  useEffect(() => {
    const unsubAuth = auth.onAuthStateChanged((user) => {
      setCurrentUserId(user?.uid || null);
    });
    return () => unsubAuth();
  }, []);

  // Fetch leaderboard from API
  const fetchFromAPI = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        if (isMountedRef.current) {
          setError("Nicht angemeldet");
          setLoading(false);
        }
        return;
      }

      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE}/api/economy/leaderboard?season_id=${seasonId}&limit=${maxEntries}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error(`API error: ${res.status}`);
      
      const data = await res.json();
      if (isMountedRef.current) {
        setEntries(data.entries || []);
        setLoading(false);
        setError(null);
      }
    } catch (err) {
      console.error("Leaderboard API error:", err);
      if (isMountedRef.current) {
        setError(err.message);
        setLoading(false);
      }
    }
  }, [seasonId, maxEntries]);

  // Setup polling for realtime updates
  useEffect(() => {
    isMountedRef.current = true;
    
    // Initial fetch
    fetchFromAPI();
    
    // Setup polling if realtime is enabled
    if (realtime) {
      pollIntervalRef.current = setInterval(() => {
        if (isMountedRef.current) {
          fetchFromAPI();
        }
      }, 2000); // Poll every 2 seconds
    }

    return () => {
      isMountedRef.current = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [fetchFromAPI, realtime]);

  const adjustCoins = useCallback(async (userId, delta) => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE}/api/economy/admin/adjust-wallet`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, delta, reason: 'Admin-Korrektur (Ranking)' }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        alert(d.detail || `Fehler ${res.status}`);
      } else {
        fetchFromAPI();
      }
    } catch (e) { alert(e.message); }
  }, [fetchFromAPI]);

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-5 h-5 text-yellow-400" />;
      case 2:
        return <Medal className="w-5 h-5 text-gray-300" />;
      case 3:
        return <Award className="w-5 h-5 text-amber-600" />;
      default:
        return <span className="w-5 text-center text-white/50">{rank}</span>;
    }
  };

  const getRankEmoji = (rank) => {
    switch (rank) {
      case 1: return "🥇";
      case 2: return "🥈";
      case 3: return "🥉";
      default: return `${rank}.`;
    }
  };

  if (loading) {
    return (
      <div className={cn("flex items-center justify-center py-8", className)}>
        <div className="animate-spin w-6 h-6 border-2 border-yellow-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("text-red-400 text-center py-4", className)}>
        {error}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className={cn("text-white/50 text-center py-4", className)}>
        Noch keine Teilnehmer
      </div>
    );
  }

  const userIdToHighlight = highlightUserId || currentUserId;

  return (
    <div className={cn("space-y-2", className)}>
      {entries.map((entry) => {
        const isCurrentUser = entry.userId === userIdToHighlight;
        
        return (
          <div
            key={entry.userId}
            className={cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg",
              "transition-all duration-200",
              isCurrentUser 
                ? "bg-yellow-500/20 border border-yellow-500/50 ring-1 ring-yellow-500/30" 
                : "bg-white/5 hover:bg-white/10",
              compact ? "py-1.5" : "py-2"
            )}
          >
            {/* Rank */}
            <div className="w-8 flex-shrink-0 text-center">
              {compact ? (
                <span className="text-lg">{getRankEmoji(entry.rank)}</span>
              ) : (
                getRankIcon(entry.rank)
              )}
            </div>

            <PlayerAvatar photoUrl={entry.photoUrl} name={entry.displayName} size={compact ? "xs" : "sm"} />

            {/* Name */}
            <div className="flex-1 min-w-0">
              <div className={cn(
                "font-medium truncate",
                isCurrentUser ? "text-yellow-300" : "text-white"
              )}>
                {entry.displayName}
                {isCurrentUser && (
                  <span className="ml-2 text-xs bg-yellow-500/30 px-1.5 py-0.5 rounded text-yellow-300">
                    DU
                  </span>
                )}
              </div>
              {showDetails && !compact && (
                <div className="text-xs text-white/40">
                  {Math.round(entry.available)} Wett-Coins{entry.earned > 0 ? ` · ⚡ ${Math.round(entry.earned)} verdient` : ''}{entry.locked > 0 ? ` · 🔒 ${Math.round(entry.locked)} Gewinne` : ''}
                </div>
              )}
            </div>

            {/* Score (Wett-Coins + gesperrte Gewinne) */}
            <div className="flex-shrink-0">
              <CoinDisplay amount={entry.score ?? entry.balance} size={compact ? "sm" : "md"} />
            </div>

            {/* Admin coin tools */}
            {adminMode && (
              <div className="flex items-center gap-1 flex-shrink-0" data-testid={`admin-coins-${entry.userId}`}>
                <button onClick={() => adjustCoins(entry.userId, -10)} className="w-6 h-6 rounded bg-red-500/20 text-red-300 text-xs hover:bg-red-500/30">−</button>
                <button onClick={() => adjustCoins(entry.userId, 10)} className="w-6 h-6 rounded bg-green-500/20 text-green-300 text-xs hover:bg-green-500/30">+</button>
                <button
                  onClick={() => {
                    const v = prompt(`Coins für ${entry.displayName} ändern (z.B. 50 oder -30):`);
                    const n = parseInt(v, 10);
                    if (!isNaN(n) && n !== 0) adjustCoins(entry.userId, n);
                  }}
                  className="px-1.5 h-6 rounded bg-white/10 text-white/70 text-[10px] hover:bg-white/20"
                >✎</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * LeaderboardStage - Full-screen leaderboard for Stage/Beamer
 */
export function LeaderboardStage({ entries = [], title = "LEADERBOARD" }) {
  const top5 = entries.slice(0, 5);
  const rest = entries.slice(5, 15);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 p-8">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-4 mb-2">
          <Trophy className="w-12 h-12 text-yellow-400" />
          <h1 className="text-5xl font-bold text-white">{title}</h1>
          <Trophy className="w-12 h-12 text-yellow-400" />
        </div>
        <div className="text-xl text-white/60">Season 2026/27</div>
      </div>

      {/* Top 5 */}
      <div className="max-w-4xl mx-auto space-y-4 mb-8">
        {top5.map((entry, index) => (
          <div
            key={entry.userId}
            className={cn(
              "flex items-center gap-6 px-8 py-6 rounded-2xl",
              "bg-gradient-to-r",
              index === 0 
                ? "from-yellow-500/30 to-yellow-600/10 border-2 border-yellow-500/50" 
                : index === 1
                  ? "from-gray-300/20 to-gray-400/5 border border-gray-400/30"
                  : index === 2
                    ? "from-amber-600/20 to-amber-700/5 border border-amber-600/30"
                    : "from-white/10 to-white/5 border border-white/10"
            )}
          >
            {/* Rank */}
            <div className="w-20 text-center">
              <span className={cn(
                "text-5xl",
                index === 0 && "text-yellow-400",
                index === 1 && "text-gray-300",
                index === 2 && "text-amber-600",
                index > 2 && "text-white/60"
              )}>
                {index === 0 ? "🥇" : index === 1 ? "🥈" : index === 2 ? "🥉" : `${entry.rank}.`}
              </span>
            </div>

            {/* Name */}
            <div className="flex-1 flex items-center gap-4">
              <PlayerAvatar photoUrl={entry.photoUrl} name={entry.displayName} size="lg" />
              <div className={cn(
                "text-3xl font-bold",
                index === 0 ? "text-yellow-300" : "text-white"
              )}>
                {entry.displayName}
              </div>
            </div>

            {/* Score */}
            <div className="text-4xl font-bold text-white">
              {(entry.score ?? entry.balance).toLocaleString('de-DE')} <span className="text-yellow-400">🪙</span>
            </div>
          </div>
        ))}
      </div>

      {/* Rest (smaller) */}
      {rest.length > 0 && (
        <div className="max-w-2xl mx-auto grid grid-cols-2 gap-2">
          {rest.map((entry) => (
            <div
              key={entry.userId}
              className="flex items-center gap-3 px-4 py-2 rounded-lg bg-white/5"
            >
              <span className="w-8 text-white/50">{entry.rank}.</span>
              <PlayerAvatar photoUrl={entry.photoUrl} name={entry.displayName} size="xs" />
              <span className="flex-1 text-white truncate">{entry.displayName}</span>
              <span className="text-white font-mono">{entry.score ?? entry.balance} 🪙</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Leaderboard;
